// ── Polymarket position close — verify-and-retry sweep ──────────
//
// Port of the Kalshi close discipline (lib/trading/closeResidual.ts) onto
// Polymarket mechanics. Polymarket has no true market order and no amend,
// so a close is a marketable-limit SELL of the held outcome token at the
// floor price, submitted FAK (fill-and-kill / IOC): it fills against the
// resting bids the limit can reach and cancels the remainder — nothing
// rests. (Bids finer than the integer-cent floor are out of reach; they
// surface as residual, never as a resting order.)
//
// TOKEN RESOLUTION IS THE CALLER'S DATA, NOT THE LEDGER'S: relay position
// rows carry the market conditionId (CLOB Trade.market), not a tokenId —
// selling requires mapping (conditionId, side) → outcome token through the
// live store (resolvePolyCloseTarget). No resolution → no order. Ever.
//
// Verification is response-driven, never poller-driven: the relay's fills
// ledger updates on a polling cadence, so re-reading /positions right
// after a close sees stale counts and a retry loop keyed off it would
// double-sell. The CLOB's POST response carries the matched amount
// (`makingAmount` = tokens sold on a matched SELL); the loop re-sweeps
// ONLY on a verified positive partial fill. Everything else — 'delayed',
// 'unmatched' (docs: "failed to delay, PLACEMENT STILL SUCCESSFUL" — not
// a kill!), empty/absent/zero/oversized amounts — is UNVERIFIED: report
// and stop. A blind full-size retry against the org's SHARED funder
// account doesn't bounce, it executes against co-traders' holdings.

import { placePolyOrderViaRelay } from './relay';
import { resolvePolyCloseTarget } from './describeOrder';
import { explainPolyError } from './errors';

/** Sweep attempt cap — parity with Kalshi's MAX_CLOSE_ATTEMPTS. */
export const MAX_POLY_CLOSE_ATTEMPTS = 3;

/** Polymarket's usual minimum order size; some markets set a higher
 *  per-market minimum (the CLOB rejection states it — decoded in the UI).
 *  Residual below this floor is treated as unsweepable dust. */
export const POLY_MIN_ORDER_CONTRACTS = 5;

export interface PolyCloseOutcome {
  /** false = the user must look at this (residual, unverified, or error). */
  ok: boolean;
  /** Auto-dismiss any confirm/toast UI only on a fully verified flat. */
  autoDismiss: boolean;
  msg: string;
  /** 0 = confirmed flat · >0 = contracts still open · null = unverified. */
  residual: number | null;
}

/** Floor sell price for the sweep — ONE TICK in cents, the market's actual
 *  lowest valid price (DNL round-3: the old 1¢ blanket floor left executable
 *  sub-cent bids unswept on 0.001/0.0001-tick markets and reported a
 *  residual). ONLY the relay-supported CLOB tick union maps (DNL final
 *  pass): 0.1 → 10¢, 0.01 → 1¢, 0.001 → 0.1¢, 0.0001 → 0.01¢. Anything
 *  else — missing, garbage, or an unsupported value like "0.005" — returns
 *  null and the close REFUSES rather than submitting a FAK at a price the
 *  relay would normalize differently downstream. */
const SUPPORTED_TICK_FLOOR_CENTS: Record<string, number> = {
  '0.1': 10,
  '0.01': 1,
  '0.001': 0.1,
  '0.0001': 0.01,
};
export function closeFloorCents(tickSize: string | undefined): number | null {
  if (tickSize === undefined) return null;
  return SUPPORTED_TICK_FLOOR_CENTS[tickSize] ?? null;
}

/**
 * Matched contracts off a CLOB order-POST response, or null when the
 * response doesn't carry a TRUSTWORTHY answer. Only `status:'matched'`
 * with a finite, POSITIVE `makingAmount` no larger than the requested
 * size counts (for a SELL, makingAmount is the maker-asset — outcome
 * tokens — actually matched). The docs' own example carries
 * `makingAmount: ""`, `Number('') === 0`, and 'unmatched' can mean
 * "placed but not delayed" — none of those may drive a retry, so they
 * all map to null. Exported for tests.
 */
export function matchedContractsFromFakResponse(
  result: unknown,
  requested: number,
): number | null {
  if (!result || typeof result !== 'object') return null;
  const r = result as Record<string, unknown>;
  const status = typeof r.status === 'string' ? r.status.toLowerCase() : null;
  if (status !== 'matched') return null;
  const raw = r.makingAmount;
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;
  if (typeof raw === 'string' && raw.trim() === '') return null;
  const making = Number(raw);
  if (!Number.isFinite(making) || making <= 0) return null;
  // A matched amount exceeding the request means the units aren't what
  // this parser assumes (e.g. raw 1e6 collateral units) — distrust it
  // rather than clamp it into a phantom "confirmed flat".
  if (making > requested * (1 + 1e-6)) return null;
  return making;
}

export interface ClosePolyPositionArgs {
  /** Relay position row `ticker` — the market conditionId. */
  conditionId: string;
  /** Which outcome token the position holds (relay row `side`). */
  side: 'yes' | 'no';
  /** Contracts currently held (relay row `netCount`). */
  quantity: number;
}

export async function closePolyPosition(
  args: ClosePolyPositionArgs,
): Promise<PolyCloseOutcome> {
  const requested = args.quantity;
  let remaining = requested;

  for (let attempt = 1; attempt <= MAX_POLY_CLOSE_ATTEMPTS; attempt++) {
    if (remaining < POLY_MIN_ORDER_CONTRACTS) {
      return {
        ok: false,
        autoDismiss: false,
        residual: remaining,
        msg:
          `CLOSE INCOMPLETE: ${remaining} contract${remaining === 1 ? '' : 's'} remain${remaining === 1 ? 's' : ''} but ` +
          `Polymarket's minimum order is ${POLY_MIN_ORDER_CONTRACTS} — this residual can't be swept. ` +
          'It settles with the market.',
      };
    }

    // FRESH resolution every attempt: the ledger's conditionId must map to
    // the held outcome token through the live store, and tickSize/negRisk
    // must ride along (defaulting negRisk on a soccer 3-way gets the sell
    // rejected with "invalid signature"). Unresolvable → refuse, explain.
    const target = resolvePolyCloseTarget(args.conditionId, args.side);
    if (!target) {
      return {
        ok: false,
        autoDismiss: false,
        residual: remaining,
        msg:
          'Close unavailable: this market is not loaded in the live feed, so the position ' +
          "can't be mapped to its sell token. Open the market's sport tab (loads it into " +
          'the store) and retry.',
      };
    }

    // Unsupported/missing tick → refuse rather than sweep at a price the
    // relay would normalize differently (DNL final pass).
    const floorCents = closeFloorCents(target.tickSize);
    if (floorCents == null) {
      return {
        ok: false,
        autoDismiss: false,
        residual: remaining,
        msg:
          `Close unavailable: this market's tick size (${target.tickSize ?? 'unknown'}) is not a ` +
          'supported CLOB tick (0.1 / 0.01 / 0.001 / 0.0001), so a safe sweep floor cannot be ' +
          'derived. Retry once the market data refreshes.',
      };
    }

    const out = await placePolyOrderViaRelay({
      tokenId: target.tokenId,
      action: 'sell',
      priceCents: floorCents,
      contracts: remaining,
      timeInForce: 'FAK',
      tickSize: target.tickSize,
      ...(target.negRisk ? { negRisk: true } : {}),
    });

    if (!out.ok) {
      return {
        ok: false,
        autoDismiss: false,
        residual: remaining,
        msg: explainPolyError(out.error),
      };
    }

    const matched = matchedContractsFromFakResponse(out.result, remaining);
    if (matched === null) {
      return {
        ok: false,
        autoDismiss: false,
        residual: null,
        msg:
          'CLOSE UNVERIFIED: the order was submitted but the response carried no trustworthy ' +
          'fill amount — the position may be fully open, partly closed, or closed. Refresh ' +
          'positions and check MyTrades before retrying; do NOT assume either way.',
      };
    }

    remaining = Math.max(0, remaining - matched);
    if (remaining === 0) {
      return {
        ok: true,
        autoDismiss: true,
        residual: 0,
        msg: `Position closed — ${requested} contract${requested === 1 ? '' : 's'} sold.`,
      };
    }
  }

  return {
    ok: false,
    autoDismiss: false,
    residual: remaining,
    msg:
      `CLOSE INCOMPLETE: ${remaining} of ${requested} contracts still open after ` +
      `${MAX_POLY_CLOSE_ATTEMPTS} sweeps — the bid book is thin. Retry, or work the ` +
      'remainder manually from the game panel.',
  };
}
